import React, { useState, useRef, useEffect } from 'react';
import gsap from 'gsap';
import { Flip } from 'gsap/Flip';

gsap.registerPlugin(Flip);

interface Service {
  id: number;
  alt: string;
  title: string;
  leftTitle: string;
  description: string;
  link: string;
  features: { text: string }[];
}

const ServicesList: React.FC = () => {
  const [activeServiceIndex, setActiveServiceIndex] = useState<number | null>(null);
  const listItemsRef = useRef<(HTMLLIElement | null)[]>([]);
  const serviceItemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const overlayItemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const overlayNavRef = useRef<HTMLDivElement>(null);
  const headingsRef = useRef<(HTMLHeadingElement | null)[]>([]);
  const pageRef = useRef<HTMLDivElement>(null);

  const openOverlay = (index: number) => {
    document.body.style.overflow = 'hidden';

    const listItem = listItemsRef.current[index];
    const serviceItem = serviceItemsRef.current[index];
    const overlayItem = overlayItemsRef.current[index];

    if (!listItem || !serviceItem || !overlayItem) return;

    setActiveServiceIndex(index);

    const title = listItem.querySelector('.main-title') as HTMLHeadingElement;
    const titleState = Flip.getState(title, { props: 'fontSize' });

    const overlayContent = overlayItem.querySelector('.overlay-row') as HTMLDivElement;

    gsap.set(overlayItem, { display: 'block' });
    gsap.fromTo(overlayContent, { autoAlpha: 0 }, { autoAlpha: 1, delay: 0.5 });

    const textTarget = overlayItem.querySelector("[data-overlay='text-target']") as HTMLDivElement;

    textTarget.appendChild(title);

    Flip.from(titleState);

    gsap.set(overlayNavRef.current, { display: 'flex' });
    if (overlayNavRef.current) {
      const navItems = Array.from(overlayNavRef.current.querySelectorAll("[data-overlay='nav-item']"));
      gsap.fromTo(
        navItems,
        {
          yPercent: 110,
        },
        {
          yPercent: 0,
          stagger: 0.1,
        }
      );
    }

    gsap.set(serviceItemsRef.current, { autoAlpha: 0 });

    if (pageRef.current) {
      gsap.to(pageRef.current, {
        duration: 0.5,
        backdropFilter: 'blur(5px)',
        ease: 'power2.out',
      });
    }

    listItemsRef.current.forEach((otherItem, i) => {
      if (i !== index) {
        const otherTitle = otherItem?.querySelector('.main-title') as HTMLHeadingElement;
        gsap.to(otherTitle, { yPercent: 100, autoAlpha: 0, duration: 0.45, delay: 0.2 - i * 0.05 });
      }
    });
  };

  const closeOverlay = () => {
    if (activeServiceIndex === null) return;

    document.body.style.overflow = 'auto';

    const overlayItem = overlayItemsRef.current[activeServiceIndex];
    const listItem = listItemsRef.current[activeServiceIndex];
    const serviceItem = serviceItemsRef.current[activeServiceIndex];

    if (!overlayItem || !listItem || !serviceItem) return;

    const title = overlayItem.querySelector("[data-overlay='text-target'] .main-title") as HTMLHeadingElement;
    const overlayContent = overlayItem.querySelector('.overlay-row') as HTMLDivElement;

    const titleState = Flip.getState(title, { props: 'fontSize' });

    if (overlayNavRef.current) {
      gsap.to(overlayNavRef.current.querySelectorAll("[data-overlay='nav-item']"), {
        yPercent: 110,
        onComplete: () => {
          if (overlayNavRef.current) {
            overlayNavRef.current.style.display = 'none';
          }
        },
      });
    }

    gsap.to(overlayContent, {
      autoAlpha: 0,
      onComplete: () => {
        overlayItem.style.display = 'none';
        if (pageRef.current) {
          gsap.to(pageRef.current, { duration: 0.5, backdropFilter: 'blur(0px)', ease: 'power2.out' });
        }
      },
    });

    listItem.querySelector('.button')?.appendChild(title);

    gsap.set(serviceItem, { autoAlpha: 1 });

    Flip.from(titleState);

    setActiveServiceIndex(null);

    gsap.to(headingsRef.current, { yPercent: 0, autoAlpha: 1, delay: 0.3, stagger: 0.05 });
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeOverlay();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeServiceIndex]);

  useEffect(() => {
    const handleResize = () => {
      const isDesktop = window.innerWidth > 909;
    
      listItemsRef.current.forEach((item) => {
        if (item) {
          const button = item.querySelector('.button') as HTMLButtonElement;
          const title = item.querySelector('.main-title') as HTMLHeadingElement;
    
          if (isDesktop) {
            button.style.pointerEvents = 'none';
            button.style.cursor = 'default';
            title.style.cursor = 'default';
            item.classList.add('no-click');
          } else {
            button.style.pointerEvents = 'auto';
            button.style.cursor = 'pointer';
            title.style.cursor = 'pointer';
            item.classList.remove('no-click');
          }
        }
      });
    
      if (window.innerWidth < 909) {
        gsap.set(serviceItemsRef.current, { autoAlpha: 1 });
      } else {
        gsap.set(serviceItemsRef.current, { autoAlpha: 0 });
        gsap.set(serviceItemsRef.current[0], { autoAlpha: 1 });
      }
    };
    
    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
}, []);
    

  useEffect(() => {
    listItemsRef.current.forEach((listItem, i) => {
      listItem?.addEventListener('mouseenter', () => {
        if (window.innerWidth > 909) {
          gsap.set(serviceItemsRef.current, { autoAlpha: 0 });
          gsap.set(serviceItemsRef.current[i], { autoAlpha: 1 });
        }
      });
    });
  }, []);

  return (
    <div className='services-slider-container'>
    </div>
  );
};

export default ServicesList;